import { useState, useEffect } from "react";
import { ArrowLeftIcon, PencilIcon } from "@heroicons/react/24/outline";
import {
  buildRegistrationFormState,
  formStateToRegistrationData,
  getRegistrationDetailFields,
  getRegistrationFieldLabel,
  getRegistrationFieldValue,
  getRegistrationFormDataKey,
  HOW_DID_YOU_HEAR_OPTIONS,
  isRegistrationTextareaField,
  validateRegistrationFormState,
} from "../utils/registrationFields";

interface RegistrationRecord {
  id: string;
  registration_data?: Record<string, unknown>;
  submitted_at?: string;
}

interface RegistrationDetailsPanelProps {
  registration: RegistrationRecord;
  registrationFields?: string[];
  customTermsConditions?: string;
  onBack: () => void;
  onUpdated?: (registration: RegistrationRecord) => void;
  getAuthToken?: () => string | null;
}

const inputClassName =
  "mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm";

export default function RegistrationDetailsPanel({
  registration,
  registrationFields,
  customTermsConditions,
  onBack,
  onUpdated,
  getAuthToken,
}: RegistrationDetailsPanelProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [formState, setFormState] = useState<Record<string, string | boolean>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const data = registration.registration_data;
  const name = [getRegistrationFieldValue(data, "first_name"), getRegistrationFieldValue(data, "last_name")]
    .filter((part) => part !== "-")
    .join(" ");
  const fields = getRegistrationDetailFields(registrationFields);
  const canEdit = Boolean(getAuthToken && onUpdated);

  useEffect(() => {
    if (isEditing) {
      setFormState(buildRegistrationFormState(data, registrationFields));
      setFieldErrors({});
      setSaveError(null);
    }
  }, [isEditing, data, registrationFields]);

  const handleFieldChange = (formKey: string, value: string | boolean) => {
    setFormState((prev) => ({ ...prev, [formKey]: value }));
    if (fieldErrors[formKey]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[formKey];
        return next;
      });
    }
  };

  const handleSave = async () => {
    const errors = validateRegistrationFormState(formState, registrationFields);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    const authToken = getAuthToken?.();
    if (!authToken) {
      setSaveError("You must be signed in to save changes.");
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/supabase/event-registrations/${registration.id}`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            registration_data: formStateToRegistrationData(formState),
          }),
        }
      );

      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result.error || "Failed to update registration");
      }

      onUpdated?.(result);
      setIsEditing(false);
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : "Failed to update registration");
    } finally {
      setSaving(false);
    }
  };

  const renderEditField = (fieldId: string) => {
    const label = getRegistrationFieldLabel(fieldId);
    const required = ["first_name", "last_name", "email", "phone"].includes(fieldId);
    const formKey = getRegistrationFormDataKey(fieldId) || fieldId;
    const error = fieldErrors[formKey];

    if (fieldId === "terms_conditions") {
      return (
        <div key={fieldId}>
          <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={Boolean(formState.termsConditions)}
              onChange={(e) => handleFieldChange("termsConditions", e.target.checked)}
              className="mt-1 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500"
            />
            <span>
              {label} {required && <span className="text-red-500">*</span>}
            </span>
          </label>
          {customTermsConditions && (
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 whitespace-pre-wrap">
              {customTermsConditions}
            </p>
          )}
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      );
    }

    if (fieldId === "how_did_you_hear") {
      return (
        <div key={fieldId}>
          <label htmlFor={formKey} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <select
            id={formKey}
            value={String(formState.howDidYouHear || "")}
            onChange={(e) => handleFieldChange("howDidYouHear", e.target.value)}
            className={inputClassName}
          >
            <option value="">Select an option</option>
            {HOW_DID_YOU_HEAR_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      );
    }

    const value = String(formState[formKey] ?? "");

    if (isRegistrationTextareaField(fieldId)) {
      return (
        <div key={fieldId}>
          <label htmlFor={formKey} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            {label} {required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            id={formKey}
            rows={3}
            value={value}
            onChange={(e) => handleFieldChange(formKey, e.target.value)}
            className={inputClassName}
          />
          {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
        </div>
      );
    }

    const inputType =
      fieldId === "email" || fieldId === "parent_email"
        ? "email"
        : fieldId === "phone" || fieldId === "parent_phone"
          ? "tel"
          : "text";

    return (
      <div key={fieldId}>
        <label htmlFor={formKey} className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
        <input
          id={formKey}
          type={inputType}
          value={value}
          onChange={(e) => handleFieldChange(formKey, e.target.value)}
          className={inputClassName}
          placeholder={fieldId === "date_of_birth" ? "MM/DD/YYYY" : undefined}
        />
        {error && <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-800 dark:hover:text-indigo-300"
        >
          <ArrowLeftIcon className="h-4 w-4 mr-1 pointer-events-none" />
          <span className="pointer-events-none">Back to registrations</span>
        </button>

        {canEdit && !isEditing && (
          <button
            type="button"
            onClick={() => setIsEditing(true)}
            className="inline-flex items-center rounded-md bg-white dark:bg-gray-700 px-3 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
          >
            <PencilIcon className="h-4 w-4 mr-1.5" />
            Edit
          </button>
        )}
      </div>

      <div className="mb-4">
        <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
          {name || "Registration Details"}
        </h4>
        {registration.submitted_at && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Submitted {new Date(registration.submitted_at).toLocaleString()}
          </p>
        )}
      </div>

      {isEditing ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSave();
          }}
          className="space-y-4 max-h-[50vh] overflow-y-auto pr-1"
        >
          {fields.map((fieldId) => renderEditField(fieldId))}

          {saveError && (
            <p className="text-sm text-red-600 dark:text-red-400">{saveError}</p>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => {
                setIsEditing(false);
                setFieldErrors({});
                setSaveError(null);
              }}
              disabled={saving}
              className="rounded-md bg-white dark:bg-gray-700 px-4 py-2 text-sm font-semibold text-gray-900 dark:text-gray-100 shadow-sm ring-1 ring-inset ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      ) : (
        <dl className="divide-y divide-gray-200 dark:divide-gray-700 border border-gray-200 dark:border-gray-600 rounded-md max-h-[50vh] overflow-y-auto">
          {fields.map((fieldId) => {
            const value = getRegistrationFieldValue(data, fieldId);
            return (
              <div
                key={fieldId}
                className="px-4 py-3 sm:grid sm:grid-cols-3 sm:gap-4 bg-white dark:bg-gray-800 even:bg-gray-50 dark:even:bg-gray-800/50"
              >
                <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">
                  {getRegistrationFieldLabel(fieldId)}
                </dt>
                <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:col-span-2 sm:mt-0 break-words">
                  {value}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}
